/**
 * Shared draft helpers for EVERY patient clinical form (generic + dedicated).
 * Nothing is written server-side until Save / Send to Office / To Be Corrected /
 * Completed — the form type travels in the URL until then.
 */

import { getFormMeta } from "../../clinicalFormCatalog";

export const NEW_FORM_ID = "new";

export const clinicalFormDraftUrl = (
  patientId,
  { formType, episodeId, visitDate, staffId, chartId } = {},
) => {
  const params = new URLSearchParams({ formType });
  if (chartId) params.set("chartId", String(chartId));
  if (episodeId) params.set("episodeId", String(episodeId));
  if (visitDate) params.set("visitDate", visitDate);
  if (staffId) params.set("staffId", String(staffId));
  return `/patients/${patientId}/forms/${NEW_FORM_ID}?${params.toString()}`;
};

/** Any catalog form can open as a draft; dedicated pages hydrate locally. */
export const isDraftableFormType = (formType) => Boolean(getFormMeta(formType));

/**
 * Build a create-API `visit` object without null placeholders.
 * Zod rejects `visitDate: null` (`Expected string, received null`); omit the
 * key or fill from scheduleDate / header date instead.
 */
export const sanitizeClinicalFormVisit = (visit, scheduleDate = null) => {
  if (!visit || typeof visit !== "object") return null;

  const episodeId =
    visit.episodeId != null && Number(visit.episodeId) > 0
      ? Number(visit.episodeId)
      : undefined;
  const staffId =
    visit.staffId != null && Number(visit.staffId) > 0
      ? Number(visit.staffId)
      : undefined;
  const rawDate =
    (typeof visit.visitDate === "string" && visit.visitDate.trim()) ||
    (typeof scheduleDate === "string" && scheduleDate.trim()) ||
    "";
  const visitDate = rawDate || undefined;

  if (!episodeId && !visitDate && staffId == null) return null;

  return {
    ...(episodeId ? { episodeId } : {}),
    ...(visitDate ? { visitDate } : {}),
    ...(staffId != null ? { staffId } : {}),
  };
};

/** Draft spec from a location search string, or null when it is unusable. */
export const readClinicalFormDraft = (search) => {
  const params = new URLSearchParams(search || "");
  const formType = params.get("formType");
  const meta = formType && isDraftableFormType(formType) ? getFormMeta(formType) : null;
  if (!meta) return null;

  const chartId = Number(params.get("chartId")) || null;
  const episodeId = Number(params.get("episodeId")) || null;
  const staffId = Number(params.get("staffId")) || null;
  const visitDate = params.get("visitDate") || null;

  return {
    formType: meta.formType,
    formName: meta.formName,
    formSection: meta.section,
    chartId,
    visit: sanitizeClinicalFormVisit({
      episodeId: episodeId || undefined,
      visitDate: visitDate || undefined,
      staffId: staffId || undefined,
    }),
  };
};

/** Request body that turns a draft into a real clinical-form parent row. */
export const clinicalFormCreatePayload = (
  draft,
  { formData, flag, status, filledByUserId, scheduleDate } = {},
) => {
  const visit = sanitizeClinicalFormVisit(draft?.visit, scheduleDate);
  return {
    formType: draft.formType,
    formData: formData ?? {},
    ...(flag ? { flag } : {}),
    ...(status && !flag ? { status } : {}),
    ...(visit ? { visit } : {}),
    ...(draft.chartId ? { chartId: draft.chartId } : {}),
    ...(filledByUserId != null ? { filledByUserId } : {}),
    ...(scheduleDate ? { scheduleDate } : {}),
  };
};
/**
 * Dedicated forms: create the parent clinical-form row (if still a draft), then
 * PUT the dedicated API so the detail row is created/updated on save — never on open.
 *
 * @returns {{ form: object, createdClinicalFormId?: number }}
 */
export async function saveDedicatedForm({
  patientId,
  clinicalForm,
  draft,
  flag,
  payload,
  createClinicalForm,
  updateDedicatedForm,
  filledByUserId = null,
  scheduleDate = null,
}) {
  let formId = clinicalForm?.id;
  let createdClinicalFormId;

  if (!formId) {
    if (!draft?.formType) {
      throw new Error("Missing draft form type");
    }
    const createdRes = await createClinicalForm(
      patientId,
      clinicalFormCreatePayload(draft, {
        formData: {},
        // Parent stays pending; dedicated PUT applies the real workflow flag.
        status: "pending",
        filledByUserId,
        scheduleDate,
      }),
    );
    const created = createdRes?.result?.form || createdRes?.form;
    if (!created?.id) throw new Error("Form was not created");
    formId = created.id;
    createdClinicalFormId = created.id;
  }

  const res = await updateDedicatedForm(patientId, formId, {
    ...payload,
    flag,
    ...(filledByUserId != null ? { filledByUserId } : {}),
  });
  const form = res?.result?.form || res?.form;
  return { form, createdClinicalFormId };
}
